import type {
  NeoConversationContentPart,
  NeoConversationRichContentPart,
} from "../../../hexagon/core/ports/driven/neoConversationRepository";
import {
  chartArtifactKey,
  dashboardArtifactKey,
  mapArtifactKey,
} from "../../../pages/Neo/artifacts";

const RICH_PART_TYPES = new Set<NeoConversationRichContentPart["type"]>([
  "neo_chart",
  "neo_map",
  "neo_form",
  "neo_dashboard",
  "neo_flag_card",
]);

export const isCopilotRichContentPart = (
  part: NeoConversationContentPart | Record<string, unknown>
): part is NeoConversationRichContentPart =>
  typeof part.type === "string" &&
  RICH_PART_TYPES.has(part.type as NeoConversationRichContentPart["type"]);

export const richContentFromParts = (
  parts: NeoConversationContentPart[] | null | undefined
): NeoConversationRichContentPart[] =>
  mergeRichContent([], (parts ?? []).filter(isCopilotRichContentPart));

const formKey = (part: NeoConversationRichContentPart): string => {
  const fields = Array.isArray(part.fields)
    ? part.fields
        .map((field) =>
          field && typeof field === "object"
            ? String((field as Record<string, unknown>).name ?? "")
            : ""
        )
        .join(",")
    : "";
  return `form|${String(part.form_type ?? "")}|${String(
    part.title ?? ""
  )}|${fields}`;
};

const flagKey = (part: NeoConversationRichContentPart): string =>
  `flag|${String(part.patient_id ?? part.patient_name ?? "")}|${String(
    part.flag_id ?? part.narrative ?? ""
  )}`;

export const richContentKey = (
  part: NeoConversationRichContentPart
): string => {
  switch (part.type) {
    case "neo_chart":
      return chartArtifactKey(part);
    case "neo_map":
      return mapArtifactKey(part);
    case "neo_dashboard":
      return dashboardArtifactKey(part);
    case "neo_form":
      return formKey(part);
    case "neo_flag_card":
      return flagKey(part);
  }
};

/** Merge optimistic tool-result artifacts with canonical message blocks.
 * Canonical copies replace optimistic copies while retaining authored order. */
export const mergeRichContent = (
  current: NeoConversationRichContentPart[] = [],
  incoming: NeoConversationRichContentPart[] = []
): NeoConversationRichContentPart[] => {
  const merged = new Map<string, NeoConversationRichContentPart>();
  current.forEach((part) => merged.set(richContentKey(part), part));
  incoming.forEach((part) => merged.set(richContentKey(part), part));
  return [...merged.values()];
};

export const textFromContentParts = (
  parts: NeoConversationContentPart[] | null | undefined
): string =>
  (parts ?? [])
    .flatMap((part) =>
      part.type === "text" && typeof part.text === "string" ? [part.text] : []
    )
    .join("");
